<template>
  <DetailView :title="locale !== 'fr' && media.titleEn ? media.titleEn : media.title" :outline="outline"
    :icon-for-type="media.type">
    <template v-if="media.work?.composers?.length > 0" #heroSubtitle>
      <i18n-t keypath="media.description_year" tag="h3">
        <template #author>
          <BasicLink v-for="composer in media.work?.composers" :key="`${composer.firstName} ${composer.lastName}`" :to="composer.slug?.includes('medias-')
            ? '#'
            : '/composer/' + composer.slug + '/biography'
            ">
            {{ composer.firstName }} {{ composer.lastName }}&nbsp;
          </BasicLink>
        </template>
        <template v-if="media.work?.publishingStartDate" #year>{{
          parseISO(media.work.publishingStartDate).getFullYear()
          }}</template>
      </i18n-t>
    </template>
    <template #heroDetails>
      <div class="first-letter:uppercase">{{ media.type }}</div>
    </template>
    <div v-if="media.confidentiality === 'private'">
      <BasicMessage :title="'Attention'"
        :description="'Cette page est cachée au public. Vous la voyez car vous êtes connecté(e) avec les droits appropriés.'" />
    </div>
    <div v-if="media?.files?.length > 0" id="audiovisual_archive" class="mt-6 md:mt-0">
      <div v-if="loggedIn || media.download" class="flex mb-4 justify-end">
        <BasicButton class="flex-1 basis-full py-1 text-sm md:flex-initial md:basis-0" @click="download">
          <template #iconLeft>
            <IconDownload class="h-3 w-3" />
          </template>
          <span class="first-letter:uppercase">{{
            $t('labels.download')
            }}</span>
        </BasicButton>
        <BasicButton class="flex-1 basis-full py-1 text-sm md:flex-initial md:basis-0 ml-4"
          v-if="media?.files?.[0]?.getUrlSource" @click="downloadSource">
          <template #iconLeft>
            <IconDownload class="h-3 w-3" />
          </template>
          <span class="first-letter:uppercase">{{
            $t('labels.download_source')
            }}</span>
        </BasicButton>
      </div>
      <div v-if="media.type?.toLowerCase() === 'audio'">
        <MediaAudio :audio="media" />
      </div>
      <div v-else>
        <MediaVideo :video="media" />
      </div>
    </div>
    <h3 id="information" class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-lg">
      {{ $t('labels.information', 2) }}
    </h3>
    <DataDescriptionList :items="information" class="mt-4" />
    <div class="mt-4" v-if="locale !== 'fr' && media.summaryEn" v-html="media.summaryEn" />
    <div class="mt-4" v-else-if="media.summary" v-html="media.summary" />
    <hr v-if="media.summary && media.collectionSummary" class="mt-8" />
    <div v-if="media.collectionSummary" class="mt-4">
      <h3 class="mb-2 mt-6 text-mobile-lg font-semibold first-letter:uppercase md:text-lg" v-if="locale === 'fr'">
        {{ media.collectionName }}
      </h3>
      <h3 class="mb-2 mt-6 text-mobile-lg font-semibold first-letter:uppercase md:text-lg" v-else>
        {{ media.collectionNameEn }}
      </h3>

      <div v-if="locale === 'fr'" v-html="media.collectionSummary" />
      <div v-else v-html="media.collectionSummaryEn" />
    </div>
    <h3 id="work_by" v-if="media.work?.composers?.length > 0"
      class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-lg">
      {{ $t('media.work_by') }}
    </h3>
    <CardPerson v-if="media.work?.composers?.length > 0" v-for="composer in media.work?.composers" :key="composer.slug"
      :person="composer" role="composer" class="mt-4 w-full md:w-fit" />
    <h3 v-if="media.participants && media.participants.length > 0" id="speakers"
      class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-lg">
      {{ $t('media.speakers') }}
    </h3>
    <div v-if="media.participants && media.participants.length > 0" class="mt-4 flex flex-wrap gap-2">
      <div v-for="(participant, i) in media.participants
        .concat(
          media.collectivities?.map(({ name, slug }) => ({
            lastName: name,
            slug,
            cardRole: 'collectivity',
          })) ?? []
        )
        .map(({ role, roleEn, ...participant }) => ({
          ...participant,
          informations: locale === 'fr' ? role : roleEn,
        }))" :key="i" class="flex-1 basis-full md:flex-initial md:basis-auto">
        <CardPerson :person="participant" :role="participant.cardRole ?? 'person'" />
      </div>
    </div>
    <div class="divide-y divide-shade-200 mt-4">
      <h3 v-if="media.relatedContent?.length > 0" id="relatedContent"
        class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-lg">
        {{ $t('labels.relatedContent') }}
      </h3>
      <ListResource v-if="media.relatedContent?.length > 0" v-for="(result, i) in media.relatedContent.map(formatMedia)"
        :key="i" :resource="result" class="py-4" />
    </div>
    <template #contentMisc>
      <hr class="mt-6 border-shade-200 md:mt-10" />
      <MiscShare />
      <hr v-if="recommendations?.length > 0" class="mt-6 border-shade-200 md:mt-10" />
      <h3 v-if="recommendations?.length > 0" id="recommendations"
        class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-lg">
        {{ $t('labels.recommendations') }}
      </h3>
      <div class="mt-6 grid grid-cols-resource-card gap-6 md:mt-10 md:gap-14">
        <CardResource v-for="card in recommendations" :key="card.id" :resource="card" />
      </div>
    </template>
  </DetailView>
</template>
<script lang="ts" setup>
import IconDownload from '~/assets/icon/general/Download.svg'
import { parseISO } from 'date-fns'
import { PropType } from 'vue'
import { useI18n } from '#i18n'

const { t, locale } = useI18n()

function formatMedia(media) {
  if (!media) return null
  let titleHtml = media.title
  if (locale.value === 'en' && media.titleEn) {
    titleHtml = media.titleEn
  }
  if (!titleHtml && locale.value === 'fr') {
    titleHtml = media.titleEn
  }
  return {
    titleHtml,
    subtitle: media.description,
    excerpt: `${media.timestamp ? formatToFullDate(media.timestamp) : ''} ${media.duration ?? ''
      }`,
    ...media,
  }
}

const props = defineProps({
  media: {
    type: Object as PropType<Media>,
    required: true,
  },
  recommendations: {
    type: Array as PropType<ResourceCards>,
    default: () => [],
  },
})

const { formatToFullDate } = useFullDateFormater()

const download = () => {
  const link = document.createElement('a')
  link.href = props.media.files[0].getUrl
  link.download = props.media.files[0].originalFileName ?? props.media.title
  link.click()
  document.body.removeChild(link)
}

const downloadSource = () => {
  const link = document.createElement('a')
  if (props.media.files[0].getUrlSource) {
    link.href = props.media.files[0].getUrlSource
    link.download = props.media.files[0].originalFileName ?? props.media.title
    link.click()
    document.body.removeChild(link)
  }
}

const information = computed<DescriptionList>(() => {
  let res = []
  if (props.media.set) {
    res.push({
      key: t('media.set'),
      value: props.media.set,
      value: {
        label: props.media.set,
        href: `/set/${props.media.setSlug}/`,
      },
    })
  }
  if (props.media.event) {
    res.push({
      key: t('media.event'),
      value: {
        label: props.media.event.title,
        href: `/event/${props.media.event.slug}/`,
      },
    })
  }
  if (props.media.tags?.length > 0) {
    res.push({
      key: 'Tags',
      value: props.media.tags.map((tag) => ({
        label: tag.label,
        href: `/tag/${tag.slug}/`,
      })),
    })
  }
  if (props.media.event?.type) {
    res.push({
      key: t('media.type'),
      value: props.media.event.type,
    })
  }
  if (props.media.event?.start) {
    res.push({
      key: t('media.performance_date'),
      value: formatToFullDate(props.media.event.start),
    })
  }
  if (props.media.place) {
    res.push({
      key: t('media.performance_location'),
      value: props.media.place,
    })
  }
  if (props.media.duration) {
    res.push({
      key: t('media.duration'),
      value: props.media.duration,
    })
  }
  if (props.media.timestamp) {
    res.push({
      key: t('labels.date'),
      value: formatToFullDate(props.media.timestamp),
    })
  }
  if (props.media.programnote?.program) {
    res.push({
      key: t('labels.program_note'),
      value: {
        label: props.media.programnote.title,
        href: props.media.programnote.program.getUrl,
      },
    })
  }
  return res
})

const runtimeConfig = useRuntimeConfig()
const domain = runtimeConfig.public.DOMAIN

const token = useCookie('ressources_user_token', {
  domain,
})
const loggedIn = !!token.value

const outline = ref<SidebarMenu>({
  items: [
    {
      id: 'audiovisual_archive',
      resourceIcon: 'media',
      anchor: 'audiovisual_archive',
      title: t('media.audiovisual_archive'),
    },
    {
      id: 'information',
      resourceIcon: 'info',
      anchor: 'information',
      title: t('media.information'),
    },
    {
      id: 'work_by',
      resourceIcon: 'human',
      anchor: 'work_by',
      title: t('media.work_by'),
    },
    {
      id: 'speakers',
      resourceIcon: 'human',
      anchor: 'speakers',
      title: t('media.speakers'),
    },
    {
      id: 'recommendations',
      resourceIcon: 'link',
      anchor: 'recommendations',
      title: t('media.recommendations'),
    },
  ],
})
</script>
